import type {
  BillingExportRateMonth,
  BillingExportRatesModel,
  BillingExportRatesResponse,
  UsageRequestLogEntry,
} from '@nadoutong/contracts';
import { SETTLEMENT_FX_MAX_MONTHS } from '@nadoutong/contracts';
import { getExportRates } from '../api/billing.ts';
import { usdMicrosFromCentsFallback } from '../components/formatters.ts';
import { isValidExportRate, moneyYuan } from './money.ts';
import type { RequestMoneyCell } from './requests.ts';
import type { ExportStop, SheetCell, SheetSpec } from './types.ts';

/**
 * Settlement-rate math for the three finance exports (#236).
 *
 * A service month is the Asia/Shanghai calendar month of an instant
 * (UTC+8, no DST). A month whose two class rates are equal converts every
 * model at that rate. When they differ, a row picks CNY-priced then
 * USD-priced from the export-rates lists. Aggregate sheets (billing
 * totals, not the usage seat sheet) have no model breakdown, so a span
 * converts at the USD-priced rate when every month in it shares that rate.
 */

const SHANGHAI_OFFSET_MS = 8 * 60 * 60 * 1000;
const MONTH_RE = /^(\d{4})-(\d{2})$/;
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const RATE_SHEET_NAME = '汇率说明';

/** Chinese headers — exports are always zh, same rule as the data sheets. */
export const RATE_SHEET_HEADERS: readonly string[] = [
  '月份',
  '规则',
  '美元计价模型汇率',
  '人民币计价模型汇率',
  '汇率日期',
  '来源',
  '说明',
];

/**
 * Aggregate workbooks convert totals at the USD-priced rate, including the
 * part that came from CNY-priced models. The request log and the usage
 * seat sheet price each amount from its month (and from the model, when
 * the month's two rates differ), so they leave this cell empty.
 */
const AGGREGATE_RATE_NOTE = '汇总金额按美元计价汇率折算，含人民币计价模型的部分以对账单为准';

/** A contract month has no single CNY reference for closing AR (#236). */
export const CNY_REFERENCE_BLANK_NOTE = '人民币参考期末应收按合同汇率不适用，已留空';

/**
 * Statement 用量 could not be rebuilt from per-model dashboard totals,
 * so the cell kept the USD-priced span rate.
 */
export const CONSUMPTION_MODEL_FALLBACK_NOTE = '对账单用量无法按模型核对，按美元计价汇率折算';

export interface MonthSpan {
  readonly from: string;
  readonly to: string;
}

/** One half-open instant slice of a Shanghai service month. */
export interface ShanghaiMonthSlice {
  readonly month: string;
  readonly from: string;
  readonly to: string;
}

export interface ExportRateBook {
  readonly from: string;
  readonly to: string;
  readonly months: readonly BillingExportRateMonth[];
  readonly byMonth: ReadonlyMap<string, BillingExportRateMonth>;
  readonly cnyPriced: ReadonlySet<string>;
  readonly usdPriced: ReadonlySet<string>;
}

/** `YYYY-MM` of an instant in Asia/Shanghai. `null` when `iso` does not parse. */
export function serviceMonth(iso: string): string | null {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  const shifted = new Date(ms + SHANGHAI_OFFSET_MS);
  return formatMonth(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1);
}

/**
 * Inclusive Shanghai months touched by the half-open instant range
 * `[fromIso, toIso)`. The last included instant is one millisecond
 * before `toIso`, so a `to` that lands on a month boundary does not
 * pull that next month in.
 */
export function monthsCoveredByInstantRange(fromIso: string, toIso: string): readonly string[] {
  const fromMs = Date.parse(fromIso);
  const toMs = Date.parse(toIso);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || fromMs >= toMs) return [];
  const start = serviceMonth(new Date(fromMs).toISOString());
  const end = serviceMonth(new Date(toMs - 1).toISOString());
  if (start === null || end === null) return [];
  return enumerateMonths(start, end);
}

/**
 * Inclusive Shanghai months of a statement period. `startDay` is
 * inclusive and `endDay` is exclusive, both `YYYY-MM-DD` already cut
 * on the Asia/Shanghai calendar (the statement contract).
 */
export function monthsInExclusiveDaySpan(startDay: string, endDay: string): readonly string[] {
  if (!YMD_RE.test(startDay) || !YMD_RE.test(endDay) || startDay >= endDay) return [];
  const lastDay = shiftYmd(endDay, -1);
  if (lastDay === null || lastDay < startDay) return [];
  return enumerateMonths(startDay.slice(0, 7), lastDay.slice(0, 7));
}

/** Inclusive `YYYY-MM` chunks of at most `maxMonths` (the export-rates cap). */
export function planRateFetches(
  from: string,
  to: string,
  maxMonths: number = SETTLEMENT_FX_MAX_MONTHS,
): readonly MonthSpan[] {
  if (!isMonth(from) || !isMonth(to) || from > to) return [];
  const span = Math.max(1, Math.floor(maxMonths));
  const chunks: MonthSpan[] = [];
  let cursor = from;
  while (cursor <= to && chunks.length < 600) {
    const remaining = inclusiveMonthCount(cursor, to);
    const take = Math.min(span, remaining);
    const chunkTo = addMonths(cursor, take - 1);
    chunks.push({ from: cursor, to: chunkTo });
    if (chunkTo >= to) break;
    cursor = addMonths(chunkTo, 1);
  }
  return chunks;
}

/**
 * Concatenate chunked export-rates bodies. Model lists are the same in
 * every response; the first body's lists win.
 */
export function mergeExportRateResponses(
  parts: readonly BillingExportRatesResponse[],
): BillingExportRatesResponse {
  const first = parts[0];
  if (first === undefined) {
    return { from: '', to: '', months: [], cnyPricedModels: [], usdPricedModels: [] };
  }
  const last = parts[parts.length - 1] ?? first;
  return {
    from: first.from,
    to: last.to,
    months: parts.flatMap((part) => part.months),
    cnyPricedModels: first.cnyPricedModels,
    usdPricedModels: first.usdPricedModels,
  };
}

export function indexExportRates(response: BillingExportRatesResponse): ExportRateBook {
  const byMonth = new Map<string, BillingExportRateMonth>();
  for (const month of response.months) {
    byMonth.set(month.month, month);
  }
  return {
    from: response.from,
    to: response.to,
    months: response.months,
    byMonth,
    cnyPriced: indexModels(response.cnyPricedModels),
    usdPriced: indexModels(response.usdPricedModels),
  };
}

/** One or more `<= 24` month calls covering `from..to` inclusive. */
export async function loadExportRates(
  fetchImpl: typeof fetch,
  from: string,
  to: string,
): Promise<ExportRateBook> {
  if (!isMonth(from) || !isMonth(to) || from > to) {
    throw new Error('export rate range is invalid');
  }
  const chunks = planRateFetches(from, to);
  const parts: BillingExportRatesResponse[] = [];
  for (const chunk of chunks) {
    parts.push(await getExportRates(fetchImpl, chunk));
  }
  return indexExportRates(mergeExportRateResponses(parts));
}

/**
 * Half-open Shanghai month slices of `[fromIso, toIso)`.
 *
 * The first slice starts at `fromIso` and the last ends at `toIso`.
 * Interior edges are `YYYY-MM-01T00:00:00+08:00`. A `to` that lands on
 * a month boundary does not open that next month.
 */
export function shanghaiMonthSlices(fromIso: string, toIso: string): readonly ShanghaiMonthSlice[] {
  const fromMs = Date.parse(fromIso);
  const toMs = Date.parse(toIso);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || fromMs >= toMs) return [];
  const slices: ShanghaiMonthSlice[] = [];
  for (const month of monthsCoveredByInstantRange(fromIso, toIso)) {
    const startMs = shanghaiMonthStartMs(month);
    const endMs = shanghaiMonthStartMs(addMonths(month, 1));
    const sliceFromMs = Math.max(fromMs, startMs);
    const sliceToMs = Math.min(toMs, endMs);
    if (!(sliceFromMs < sliceToMs)) continue;
    slices.push({
      month,
      from: sliceFromMs === fromMs ? fromIso : new Date(sliceFromMs).toISOString(),
      to: sliceToMs === toMs ? toIso : new Date(sliceToMs).toISOString(),
    });
  }
  return slices;
}

/**
 * Statement period → Shanghai month slices. `startDay` is inclusive and
 * `endDay` is exclusive, both `YYYY-MM-DD` on the Asia/Shanghai calendar.
 */
export function statementMonthSlices(
  startDay: string,
  endDay: string,
): readonly ShanghaiMonthSlice[] {
  const fromMs = shanghaiDayStartMs(startDay);
  const toMs = shanghaiDayStartMs(endDay);
  if (fromMs === null || toMs === null) return [];
  return shanghaiMonthSlices(new Date(fromMs).toISOString(), new Date(toMs).toISOString());
}

/**
 * Rate for one model in one service month.
 *
 * Equal class rates skip the model lists: an unclassified model still
 * converts, because both lists would have produced the same number.
 * Otherwise the CNY-priced list wins, then the USD-priced list.
 * `{ok:false}` means the model is in neither list (or the equal rate
 * is not a usable rate). Callers reject an unavailable month first.
 */
export function rateForModel(
  entry: BillingExportRateMonth,
  book: ExportRateBook,
  vendor: string,
  model: string,
): { readonly ok: true; readonly rate: number } | { readonly ok: false } {
  if (entry.usdPricedCnyPerUsd === entry.cnyPricedCnyPerUsd) {
    if (!isValidExportRate(entry.usdPricedCnyPerUsd)) return { ok: false };
    return { ok: true, rate: entry.usdPricedCnyPerUsd };
  }
  const key = modelKey(vendor, model);
  const rate = book.cnyPriced.has(key)
    ? entry.cnyPricedCnyPerUsd
    : book.usdPriced.has(key)
      ? entry.usdPricedCnyPerUsd
      : null;
  if (!isValidExportRate(rate)) return { ok: false };
  return { ok: true, rate };
}

/**
 * One rate for a usage window that does not need a per-month read.
 *
 * Every covered month must price both model classes the same, and every
 * month must share that rate. The seat sheet can then convert each
 * whole-range micros total once — the pre-#236 shape.
 */
export function uniformUsageRate(
  months: readonly string[],
  book: ExportRateBook,
): { readonly ok: true; readonly rate: number } | { readonly ok: false } {
  let shared: number | null = null;
  for (const month of months) {
    const entry = book.byMonth.get(month);
    if (entry === undefined || entry.available !== true) return { ok: false };
    if (entry.usdPricedCnyPerUsd !== entry.cnyPricedCnyPerUsd) return { ok: false };
    const rate = entry.usdPricedCnyPerUsd;
    if (!isValidExportRate(rate)) return { ok: false };
    if (shared === null) shared = rate;
    else if (rate !== shared) return { ok: false };
  }
  if (shared === null) return { ok: false };
  return { ok: true, rate: shared };
}

/** Sum micros per rate, then convert once. One bucket matches `moneyYuan` of the total. */
export function yuanForMicrosByRate(byRate: ReadonlyMap<number, number>): number {
  let yuan = 0;
  for (const [rate, micros] of byRate) {
    yuan += moneyYuan(micros, null, rate);
  }
  return yuan;
}

export function addMicrosAtRate(byRate: Map<number, number>, rate: number, micros: number): void {
  if (micros === 0) return;
  byRate.set(rate, (byRate.get(rate) ?? 0) + micros);
}

/** True when any month in the span prices the two model classes differently. */
export function spanHasSplitRates(months: readonly string[], book: ExportRateBook): boolean {
  for (const month of months) {
    const entry = book.byMonth.get(month);
    if (entry !== undefined && entry.usdPricedCnyPerUsd !== entry.cnyPricedCnyPerUsd) return true;
  }
  return false;
}

/** Covered months that must not be converted. Missing months count. */
export function unavailableMonths(
  months: readonly string[],
  book: ExportRateBook,
): readonly string[] {
  return sortedUnique(
    months.filter((month) => {
      const entry = book.byMonth.get(month);
      if (entry === undefined || entry.available !== true) return true;
      return (
        !isValidExportRate(entry.usdPricedCnyPerUsd) || !isValidExportRate(entry.cnyPricedCnyPerUsd)
      );
    }),
  );
}

/**
 * One USD-priced rate for an aggregate amount that spans `months`.
 *
 * Every month must share the same `usdPricedCnyPerUsd`. When they do
 * not, `months` is the whole span. A missing, unpublished, or invalid
 * rate fails the span too; the export runner reports those months as
 * `rate_unavailable` before it plans spans.
 */
export function resolveAggregateRate(
  months: readonly string[],
  book: ExportRateBook,
):
  | { readonly ok: true; readonly rate: number }
  | { readonly ok: false; readonly months: readonly string[] } {
  const rates: number[] = [];
  for (const month of months) {
    const entry = book.byMonth.get(month);
    const usd = entry?.usdPricedCnyPerUsd ?? null;
    if (
      entry === undefined ||
      entry.available !== true ||
      !isValidExportRate(usd) ||
      !isValidExportRate(entry.cnyPricedCnyPerUsd)
    ) {
      return { ok: false, months: sortedUnique(months) };
    }
    rates.push(usd);
  }
  const first = rates[0];
  if (first === undefined) return { ok: false, months: [] };
  if (!rates.every((rate) => rate === first)) return { ok: false, months: sortedUnique(months) };
  return { ok: true, rate: first };
}

export function planSpanRates(
  spans: readonly (readonly string[])[],
  book: ExportRateBook,
):
  | { readonly status: 'ok'; readonly rates: readonly number[] }
  | Extract<ExportStop, { status: 'aggregate_cross_month_rate' }> {
  const offending: string[] = [];
  const rates: number[] = [];
  for (const span of spans) {
    if (span.length === 0) {
      throw new Error('aggregate span has no service month');
    }
    const resolved = resolveAggregateRate(span, book);
    if (!resolved.ok) {
      offending.push(...resolved.months);
      continue;
    }
    rates.push(resolved.rate);
  }
  if (offending.length > 0) {
    return { status: 'aggregate_cross_month_rate', months: sortedUnique(offending) };
  }
  return { status: 'ok', rates };
}

/**
 * Per-row yuan and the rate that produced it.
 *
 * Pending rows stay empty. A zero resolved cost is `0` and does not
 * consult a model list. A positive cost uses {@link rateForModel}:
 * equal class rates skip the lists, otherwise CNY-priced then
 * USD-priced. A model in neither list is reported rather than guessed.
 * `coveredMonths` is every month the export spans — one unavailable
 * month stops the export even when no row needed that month.
 */
export function planRequestMoney(
  rows: readonly UsageRequestLogEntry[],
  book: ExportRateBook,
  coveredMonths: readonly string[],
): { readonly status: 'ok'; readonly cells: readonly RequestMoneyCell[] } | ExportStop {
  const unavailable = unavailableMonths(coveredMonths, book);
  if (unavailable.length > 0) return { status: 'rate_unavailable', months: unavailable };

  const unknown: string[] = [];
  const seenUnknown = new Set<string>();
  const rowUnavailable: string[] = [];
  const cells: RequestMoneyCell[] = [];

  for (const row of rows) {
    if ((row.ratingStatus ?? 'rated') === 'pending') {
      cells.push({ yuan: null, rate: null });
      continue;
    }
    const micros = usdMicrosFromCentsFallback(row.costUsdMicros, row.costUsdCents);
    if (micros === 0) {
      cells.push({ yuan: 0, rate: null });
      continue;
    }
    const month = serviceMonth(row.createdAt);
    if (month === null) throw new Error('request row has no service month');
    const entry = book.byMonth.get(month);
    if (
      entry === undefined ||
      entry.available !== true ||
      !isValidExportRate(entry.usdPricedCnyPerUsd) ||
      !isValidExportRate(entry.cnyPricedCnyPerUsd)
    ) {
      rowUnavailable.push(month);
      cells.push({ yuan: null, rate: null });
      continue;
    }
    const picked = rateForModel(entry, book, row.vendor, row.model);
    if (!picked.ok) {
      const label = `${row.vendor}/${row.model}`;
      if (!seenUnknown.has(label)) {
        seenUnknown.add(label);
        unknown.push(label);
      }
      cells.push({ yuan: null, rate: null });
      continue;
    }
    cells.push({
      yuan: moneyYuan(row.costUsdMicros, row.costUsdCents, picked.rate),
      rate: picked.rate,
    });
  }

  if (rowUnavailable.length > 0) {
    return { status: 'rate_unavailable', months: sortedUnique(rowUnavailable) };
  }
  if (unknown.length > 0) return { status: 'unknown_models', models: unknown };
  return { status: 'ok', cells };
}

export function monthsInBook(
  months: readonly string[],
  book: ExportRateBook,
): readonly BillingExportRateMonth[] {
  const out: BillingExportRateMonth[] = [];
  for (const month of sortedUnique(months)) {
    const entry = book.byMonth.get(month);
    if (entry !== undefined) out.push(entry);
  }
  return out;
}

export function assembleRateSheet(
  months: readonly BillingExportRateMonth[],
  options: {
    readonly aggregateAmounts: boolean;
    /** Extra 说明 lines per `YYYY-MM`, after the aggregate note, joined with `；`. */
    readonly extraNotes?: ReadonlyMap<string, readonly string[]>;
  },
): SheetSpec {
  return {
    name: RATE_SHEET_NAME,
    rows: [RATE_SHEET_HEADERS, ...months.map((month) => rateRow(month, options))],
  };
}

function rateRow(
  month: BillingExportRateMonth,
  options: {
    readonly aggregateAmounts: boolean;
    readonly extraNotes?: ReadonlyMap<string, readonly string[]>;
  },
): readonly SheetCell[] {
  const lines: string[] = [];
  if (options.aggregateAmounts && month.usdPricedCnyPerUsd !== month.cnyPricedCnyPerUsd) {
    lines.push(AGGREGATE_RATE_NOTE);
  }
  const extra = options.extraNotes?.get(month.month);
  if (extra !== undefined) {
    for (const line of extra) lines.push(line);
  }
  return [
    month.month,
    month.rule,
    month.usdPricedCnyPerUsd,
    month.cnyPricedCnyPerUsd,
    month.rateDate,
    month.source,
    lines.length > 0 ? lines.join('；') : null,
  ];
}

function indexModels(models: readonly BillingExportRatesModel[]): ReadonlySet<string> {
  const set = new Set<string>();
  for (const model of models) set.add(modelKey(model.vendor, model.model));
  return set;
}

function modelKey(vendor: string, model: string): string {
  return `${vendor}\0${model}`;
}

export function enumerateMonths(from: string, to: string): readonly string[] {
  if (!isMonth(from) || !isMonth(to) || from > to) return [];
  const out: string[] = [];
  let cursor = from;
  while (cursor <= to && out.length < 1_200) {
    out.push(cursor);
    if (cursor === to) break;
    cursor = addMonths(cursor, 1);
  }
  return out;
}

function inclusiveMonthCount(from: string, to: string): number {
  const start = monthIndex(from);
  const end = monthIndex(to);
  if (start === null || end === null) return 0;
  return end - start + 1;
}

function addMonths(ym: string, delta: number): string {
  const index = monthIndex(ym);
  if (index === null) throw new Error('export rate month is invalid');
  const next = index + delta;
  return formatMonth(Math.floor(next / 12), (next % 12) + 1);
}

function monthIndex(ym: string): number | null {
  const match = MONTH_RE.exec(ym);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || month < 1 || month > 12) return null;
  return year * 12 + (month - 1);
}

function isMonth(ym: string): boolean {
  return monthIndex(ym) !== null;
}

function formatMonth(year: number, month: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;
}

function shanghaiMonthStartMs(month: string): number {
  const index = monthIndex(month);
  if (index === null) throw new Error('export rate month is invalid');
  const year = Math.floor(index / 12);
  const monthIndex0 = index % 12;
  return Date.UTC(year, monthIndex0, 1) - SHANGHAI_OFFSET_MS;
}

/** `YYYY-MM-DD` at 00:00 Asia/Shanghai, or `null` when the day is not real. */
function shanghaiDayStartMs(ymd: string): number | null {
  const match = YMD_RE.exec(ymd);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const utcMidnight = Date.UTC(year, month - 1, day);
  const check = new Date(utcMidnight);
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day
  ) {
    return null;
  }
  return utcMidnight - SHANGHAI_OFFSET_MS;
}

function shiftYmd(ymd: string, deltaDays: number): string | null {
  const match = YMD_RE.exec(ymd);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  date.setUTCDate(date.getUTCDate() + deltaDays);
  return date.toISOString().slice(0, 10);
}

function sortedUnique(items: Iterable<string>): string[] {
  return [...new Set(items)].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}
